class MultiTabber extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabButtons = this.querySelectorAll('.multi-tabber-tab-btn');
    this.panels = this.querySelectorAll('.multi-tabber-panel');

    this.bindEvents();
    this.initializeActiveSlider();
  }

  bindEvents() {
    this.tabButtons.forEach((btn, index) => {
      const pointerDownHandler = (e) => {
        if (e.pointerType === 'mouse' || e.pointerType === 'touch' || e.pointerType === 'pen') {
          e.preventDefault();
        }
      };
      btn.addEventListener('pointerdown', pointerDownHandler);
      btn._mtPointerDownHandler = pointerDownHandler;

      const clickHandler = (e) => {
        this.activateTab(index);
        if (e.detail !== 0) {
          btn.blur();
        }
      };
      btn.addEventListener('click', clickHandler);
      btn._mtClickHandler = clickHandler;

      const keydownHandler = (e) => {
        let nextIndex = null;
        switch (e.key) {
          case 'ArrowRight':
            nextIndex = (index + 1) % this.tabButtons.length;
            break;
          case 'ArrowLeft':
            nextIndex = (index - 1 + this.tabButtons.length) % this.tabButtons.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabButtons.length - 1;
            break;
        }
        if (nextIndex !== null) {
          e.preventDefault();
          this.tabButtons[nextIndex].focus();
          this.activateTab(nextIndex);
        }
      };
      btn.addEventListener('keydown', keydownHandler);
      btn._mtKeydownHandler = keydownHandler;
    });
  }

  disconnectedCallback() {
    // Remove previously attached listeners using stored references
    if (this.tabButtons) {
      this.tabButtons.forEach(btn => {
        if (btn._mtPointerDownHandler) {
          btn.removeEventListener('pointerdown', btn._mtPointerDownHandler);
          delete btn._mtPointerDownHandler;
        }
        if (btn._mtClickHandler) {
          btn.removeEventListener('click', btn._mtClickHandler);
          delete btn._mtClickHandler;
        }
        if (btn._mtKeydownHandler) {
          btn.removeEventListener('keydown', btn._mtKeydownHandler);
          delete btn._mtKeydownHandler;
        }
      });
    }
  }

  activateTab(activeIndex) {
    let prevActive = -1;

    this.tabButtons.forEach((btn, i) => {
      if (btn.getAttribute('aria-selected') === 'true') prevActive = i;
    });

    this.tabButtons.forEach((btn, i) => {
      const isSelected = i === activeIndex;
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      btn.setAttribute('tabindex', isSelected ? '0' : '-1');
      if (!isSelected) return;

      const target =
        prevActive !== -1 && activeIndex > prevActive
          ? this.tabButtons[i + 1] || btn
          : prevActive !== -1 && activeIndex < prevActive
            ? this.tabButtons[i - 1] || btn
            : btn;

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === activeIndex;

      if (isActive) {
        panel.classList.remove('active');
        void panel.offsetWidth;
        panel.classList.add('active');

        const slider = panel.querySelector(
          '[data-multi-tabber-slider]'
        );

        if (slider) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.initializeSwiper(slider, panel);
            });
          });
        }
      } else {
        panel.classList.remove('active');
      }
    });
  }

  initializeActiveSlider() {
    this.panels.forEach((panel) => {
      if (!panel.classList.contains('active')) return;

      const slider = panel.querySelector(
        '[data-multi-tabber-slider]'
      );

      if (slider) {
        this.initializeSwiper(slider, panel);
      }
    });
  }

  initializeSwiper(slider, panel) {
    if (!slider) return;

    const run = () => {
      if (!slider.swiper) return;

      slider.swiper.update();
      this.bindSliderNavigation(slider, panel);
      if (typeof slider.setupSlideVisibility === 'function') {
        slider.setupSlideVisibility();
      }
    };

    if (slider.swiper) {
      run();
      return;
    }

    slider.addEventListener('swiper:init', run, { once: true });
  }

  bindSliderNavigation(slider, panel) {
    if (!slider?.swiper || slider.dataset.navBound === 'true') {
      return;
    }

    const prevEl = panel.querySelector('.multi-tabber-nav--prev');
    const nextEl = panel.querySelector('.multi-tabber-nav--next');
    if (!prevEl || !nextEl) return;

    const updateState = () => {
      const { isBeginning, isEnd } = slider.swiper;
      prevEl.disabled = isBeginning;
      nextEl.disabled = isEnd;
      prevEl.classList.toggle('is-disabled', isBeginning);
      nextEl.classList.toggle('is-disabled', isEnd);
    };

    prevEl.addEventListener('click', () => slider.swiper.slidePrev());
    nextEl.addEventListener('click', () => slider.swiper.slideNext());
    slider.swiper.on('slideChange', updateState);
    slider.swiper.on('transitionEnd', updateState);
    slider.swiper.on('resize', updateState);
    updateState();
    slider.dataset.navBound = 'true';
  }
}

if (!customElements.get('multi-tabber')) {
  customElements.define('multi-tabber', MultiTabber);
}
